import type { ReactNode } from "react";
import { Link, type LinkProps } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";

type CtaLink = { label: ReactNode } & Pick<LinkProps, "to" | "params">;

/**
 * Large centred closing call to action on a soft gradient: heading, short
 * pitch, a primary (arrow) and secondary button, then optional extras such as
 * a footer link row. Used by city hubs, Rangpur area pages and the Bengali
 * About page.
 */
export function CtaPanel({
  title,
  body,
  primary,
  secondary,
  children,
}: {
  title: ReactNode;
  body: ReactNode;
  primary: CtaLink;
  secondary: CtaLink;
  children?: ReactNode;
}) {
  return (
    <div className="rounded-3xl border border-border bg-gradient-to-br from-primary/15 via-card to-card p-12 lg:p-16 text-center">
      <h2 className="font-display text-3xl md:text-5xl leading-tight">{title}</h2>
      <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">{body}</p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link
          to={primary.to}
          params={primary.params}
          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3.5 text-sm font-medium hover:opacity-90"
        >
          {primary.label} <ArrowUpRight size={16} />
        </Link>
        <Link
          to={secondary.to}
          params={secondary.params}
          className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-3.5 text-sm font-medium hover:bg-card"
        >
          {secondary.label}
        </Link>
      </div>
      {children}
    </div>
  );
}
